// ═══════════════════════════════════════════════════════════
// Bitrix24 CRM Field Name Mappings
// ═══════════════════════════════════════════════════════════

/**
 * Sentinel fieldType value for product-row aggregate totals used as a
 * calculation basis (e.g. totalAmount, totalBeforeTax).
 * These are resolved from ProductRowTotals, NOT from entity.fields or a real CRM field picker.
 */
export const COMPUTED_PRODUCT_TOTAL_FIELD_TYPE = "computed_product_total" as const;

/** CRM Entity Type IDs (from crm.enum.ownertype) */
export const ENTITY_TYPE_IDS = {
  LEAD: 1,
  DEAL: 2,
  CONTACT: 3,
  COMPANY: 4,
  INVOICE_OLD: 5,
  SMART_INVOICE: 31,
  QUOTE: 7,
} as const;

// ═══════════════════════════════════════════════════════════
// Entity Product Support — Built-in Map
// ═══════════════════════════════════════════════════════════

import type { EntityProductCapability } from "../types/commission";

/**
 * Built-in entity product support map.
 * Custom SPAs (entityTypeId >= 128) require runtime detection via crm.type.getByEntityTypeId.
 */
export const BUILT_IN_PRODUCT_SUPPORT: Record<number, EntityProductCapability> = {
  [ENTITY_TYPE_IDS.LEAD]: {
    supportsProducts: true,
    hasTaxValueField: false, // Leads do NOT support TAX_VALUE
    ownerTypeCode: "L",
    fetchMethod: "crm.item.productrow.list",
  },
  [ENTITY_TYPE_IDS.DEAL]: {
    supportsProducts: true,
    hasTaxValueField: true,
    ownerTypeCode: "D",
    fetchMethod: "crm.item.productrow.list",
  },
  [ENTITY_TYPE_IDS.QUOTE]: {
    supportsProducts: true,
    hasTaxValueField: true,
    ownerTypeCode: "Q",
    fetchMethod: "crm.item.productrow.list",
  },
  [ENTITY_TYPE_IDS.SMART_INVOICE]: {
    supportsProducts: true,
    hasTaxValueField: true,
    ownerTypeCode: "SI",
    fetchMethod: "crm.item.productrow.list",
  },
  // Contact (3) and Company (4) do NOT support products
  [ENTITY_TYPE_IDS.CONTACT]: {
    supportsProducts: false,
    hasTaxValueField: false,
    ownerTypeCode: "",
    fetchMethod: "crm.item.productrow.list",
  },
  [ENTITY_TYPE_IDS.COMPANY]: {
    supportsProducts: false,
    hasTaxValueField: false,
    ownerTypeCode: "",
    fetchMethod: "crm.item.productrow.list",
  },
};

/** Stage Semantics */
export const STAGE_SEMANTICS = {
  /** In progress — no commission trigger */
  PROCESS: null,
  /** Won/Paid — triggers commission calculation */
  SUCCESS: "S",
  /** Lost/Cancelled — triggers clawback/void */
  FAILURE: "F",
} as const;

/**
 * Registered webhook events — only native CRM entity events are listed here.
 * crm.item entities (Smart Invoice, custom SPAs, estimates) use the dynamic
 * format ONCRMDYNAMICITEM{ADD|UPDATE|DELETE}_{entityTypeId} and are registered
 * on a per-entity basis by deriveEventsFromRules().
 */
export const REGISTERED_EVENTS = ["ONCRMDEALADD", "ONCRMDEALUPDATE", "ONCRMDEALDELETE"] as const;

export type RegisteredEvent = (typeof REGISTERED_EVENTS)[number];

// ═══════════════════════════════════════════════════════════
// Entity → B24 Event Name Mapping
// ═══════════════════════════════════════════════════════════

/**
 * Maps a Bitrix24 entityTypeId to the three canonical B24 event names
 * for that entity (ADD / UPDATE / DELETE).
 *
 * Native CRM entities (Lead, Deal, Contact, Company, Quote) use their own
 * event prefix (e.g. ONCRMDEAL*, ONCRMLEAD*).
 *
 * ALL crm.item entities — Smart Invoice (31), estimates, and every custom SPA
 * (entityTypeId >= 128) — use the per-entity dynamic event format:
 *   ONCRMDYNAMICITEM{ADD|UPDATE|DELETE}_{entityTypeId}
 * e.g.:  ONCRMDYNAMICITEMUPDATE_31   (Smart Invoice)
 *        ONCRMDYNAMICITEMUPDATE_142  (Custom SPA #142)
 *
 * This is the actual format B24 fires and that event.bind / event.unbind
 * expects — verified via BX24.callMethod("event.get", {}) in the B24 console.
 */
export function getEntityEventNames(entityTypeId: number): {
  add: string;
  update: string;
  delete: string;
} {
  // Native CRM entities with dedicated event prefixes
  const KNOWN: Record<number, { add: string; update: string; delete: string }> = {
    1: { add: "ONCRMLEADADD", update: "ONCRMLEADUPDATE", delete: "ONCRMLEADDELETE" },
    2: { add: "ONCRMDEALADD", update: "ONCRMDEALUPDATE", delete: "ONCRMDEALDELETE" },
    3: { add: "ONCRMCONTACTADD", update: "ONCRMCONTACTUPDATE", delete: "ONCRMCONTACTDELETE" },
    4: { add: "ONCRMCOMPANYADD", update: "ONCRMCOMPANYUPDATE", delete: "ONCRMCOMPANYDELETE" },
    7: { add: "ONCRMQUOTEADD", update: "ONCRMQUOTEUPDATE", delete: "ONCRMQUOTEDELETE" },
  };

  if (KNOWN[entityTypeId]) {
    return KNOWN[entityTypeId];
  }

  // All crm.item entities (Smart Invoice=31, custom SPAs, etc.) use the
  // ONCRMDYNAMICITEM{ACTION}_{entityTypeId} format.
  return {
    add: `ONCRMDYNAMICITEMADD_${entityTypeId}`,
    update: `ONCRMDYNAMICITEMUPDATE_${entityTypeId}`,
    delete: `ONCRMDYNAMICITEMDELETE_${entityTypeId}`,
  };
}

/**
 * Given a list of rule trigger configs, return the full set of B24 event
 * names that must be bound to receive all relevant CRM changes.
 *
 * Clawback-aware: if a rule has `clawbackConfig` set, UPDATE is always
 * included (required for stage-change clawback). If `clawbackOnDelete` is
 * true, DELETE is also included even if not listed in `events[]`.
 */
export function deriveEventsFromRules(
  rules: Array<{
    entityTypeId?: number;
    events?: string[];
    clawbackConfig?: { clawbackOnDelete?: boolean } | null;
  }>,
): Set<string> {
  const required = new Set<string>();

  for (const rule of rules) {
    const entityTypeId = rule.entityTypeId;
    if (!entityTypeId) continue;

    const names = getEntityEventNames(entityTypeId);
    for (const ev of rule.events ?? []) {
      if (ev === "ADD") required.add(names.add);
      if (ev === "UPDATE") required.add(names.update);
      if (ev === "DELETE") required.add(names.delete);
    }

    // Clawback requires UPDATE for stage-change detection
    if (rule.clawbackConfig) {
      required.add(names.update);
    }

    // clawbackOnDelete requires DELETE to receive entity deletion events
    if (rule.clawbackConfig?.clawbackOnDelete === true) {
      required.add(names.delete);
    }
  }

  return required;
}

// ═══════════════════════════════════════════════════════════
// Invoice (Smart Invoice / crm.item) Field Map
// ═══════════════════════════════════════════════════════════

/** CRM Smart Invoice field map: camelCase API → UPPER_CASE DB */
export const INVOICE_FIELD_MAP: Record<string, string> = {
  id: "ID",
  title: "TITLE",
  stageId: "STAGE_ID",
  stageSemanticId: "STAGE_SEMANTIC_ID",
  categoryId: "CATEGORY_ID",
  opportunity: "OPPORTUNITY",
  currencyId: "CURRENCY_ID",
  taxValue: "TAX_VALUE",
  companyId: "COMPANY_ID",
  contactId: "CONTACT_ID",
  assignedById: "ASSIGNED_BY_ID",
  createdBy: "CREATED_BY",
  begindate: "BEGINDATE",
  closedate: "CLOSEDATE",
  accountNumber: "ACCOUNT_NUMBER",
  mycompanyId: "MYCOMPANY_ID",
  sourceId: "SOURCE_ID",
  observers: "OBSERVERS",
} as const;

/** Reverse map: UPPER_CASE → camelCase */
export const INVOICE_FIELD_MAP_REVERSE: Record<string, string> = Object.fromEntries(
  Object.entries(INVOICE_FIELD_MAP).map(([k, v]) => [v, k]),
);
